import React from "react";
import { Link } from "react-router-dom";
import { artist, totals, services, specialties, designTools, aiTools, aiStatement } from "../mock";
import { ArrowRight, Sparkles } from "lucide-react";

const timeline = [
  { year: "2005", title: "Started as a junior designer", note: "Cutting my teeth on print, editorial, and early digital." },
  { year: "2010", title: "Agency life — national campaigns", note: "Direct mail, multilingual charity work, and Fortune 500 collateral." },
  { year: "2016", title: "Senior role, global events", note: "Product launches across NYC, Hong Kong, Singapore, and Amsterdam." },
  { year: "2020", title: "Rebrand specialist", note: "Led collateral rollout for merged tech-media giants." },
  { year: "2024", title: "Independent practice", note: "Currently partnering with select brands and agencies on high-impact creative." }
];

const AboutPage = () => {
  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-24">
      <header className="grid md:grid-cols-12 gap-10 items-end mb-16 md:mb-20 fade-up">
        <div className="md:col-span-8">
          <p className="font-mono-label text-[#F62493] mb-5">About</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-[-0.02em] text-stone-100">
            {artist.experience} of craft, <em className="italic text-[#F62493]">delivered with a smile.</em>
          </h1>
        </div>
        <div className="md:col-span-4">
          <p className="text-lg text-stone-200 leading-relaxed">{artist.bio}</p>
        </div>
      </header>

      {/* PORTRAIT FEATURE */}
      <section className="grid md:grid-cols-12 gap-8 md:gap-12 mb-20 md:mb-28 fade-up delay-1">
        <div className="md:col-span-5 lg:col-span-4">
          <div className="relative aspect-[4/5] overflow-hidden bg-[#242424]">
            <img
              src={artist.photo}
              alt={artist.name}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
          <div className="mt-4 flex items-baseline justify-between gap-6">
            <p className="font-mono-label text-stone-300">Portrait — {artist.location}</p>
            <p className="font-mono-label text-stone-300">Est. 2005</p>
          </div>
        </div>
        <div className="md:col-span-7 lg:col-span-8 flex flex-col justify-center">
          <p className="font-mono-label text-stone-300 mb-4">In Her Own Words</p>
          <blockquote className="font-display text-3xl md:text-4xl lg:text-5xl text-stone-100 leading-[1.15] tracking-[-0.01em]">
            &ldquo;Great design is equal parts <em className="italic text-[#F62493]">strategy</em>, <em className="italic text-[#F62493]">craft</em>, and <em className="italic text-[#F62493]">empathy</em> — wrapped in a sense of humor that makes the work fun to make and even better to receive.&rdquo;
          </blockquote>
          <p className="mt-8 font-mono-label text-stone-300">
            — {artist.name}, {artist.title}
          </p>
        </div>
      </section>

      {/* STATS */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8 pb-20 md:pb-28 border-b border-stone-800">
        {[
          { n: totals.years + "+", l: "Years of experience" },
          { n: totals.clients, l: "Client brands" },
          { n: totals.projects + "+", l: "Projects delivered" },
          { n: totals.countries, l: "Countries reached" }
        ].map((s) => (
          <div key={s.l}>
            <div className="font-display text-5xl md:text-7xl text-stone-100">{s.n}</div>
            <div className="font-mono-label text-stone-300 mt-3">{s.l}</div>
          </div>
        ))}
      </section>

      {/* TIMELINE */}
      <section className="py-20 md:py-28">
        <p className="font-mono-label text-stone-300 mb-6">The Path</p>
        <h2 className="font-display text-4xl md:text-6xl text-stone-100 mb-14 max-w-3xl">
          Two decades, one through-line: design that moves the business.
        </h2>
        <div className="space-y-0">
          {timeline.map((t, i) => (
            <div
              key={t.year}
              className="grid md:grid-cols-12 gap-4 md:gap-10 py-8 border-t border-stone-800 last:border-b"
            >
              <div className="md:col-span-2 font-mono-label text-stone-300 pt-1">{t.year}</div>
              <div className="md:col-span-5">
                <h3 className="font-display text-2xl md:text-3xl text-stone-100">{t.title}</h3>
              </div>
              <p className="md:col-span-5 text-stone-200 leading-relaxed">{t.note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SPECIALTIES — DEEP DIVES */}
      <section className="py-20 md:py-28 border-t border-stone-800">
        <div className="grid md:grid-cols-12 gap-8 mb-12 md:mb-16">
          <div className="md:col-span-5">
            <p className="font-mono-label text-stone-300 mb-4">Areas of Depth</p>
            <h2 className="font-display text-4xl md:text-5xl text-stone-100 leading-tight">
              Where I go <em className="italic text-[#F62493]">deep</em>.
            </h2>
          </div>
          <p className="md:col-span-7 text-lg text-stone-200 leading-relaxed">
            Beyond the broad capabilities, these are the specialties where I bring the most experience — the work I'm hired for again and again, and the rooms I want to be in.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-10">
          {specialties.map((s, i) => (
            <article
              key={s.title}
              className="group border-t-2 border-stone-900 pt-6 md:pt-8 transition-colors hover:border-[#F62493]"
            >
              <p className="font-mono-label text-stone-300 mb-4">
                {String(i + 1).padStart(2, "0")} / {String(specialties.length).padStart(2, "0")}
              </p>
              <h3 className="font-display text-2xl md:text-3xl text-stone-100 leading-tight mb-5 group-hover:text-[#F62493] transition-colors">
                {s.title}
              </h3>
              <p className="text-stone-200 leading-relaxed">{s.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* TOOLKIT */}
      <section className="py-20 md:py-28 border-t border-stone-800">
        <div className="grid md:grid-cols-12 gap-8 mb-12 md:mb-16">
          <div className="md:col-span-5">
            <p className="font-mono-label text-stone-300 mb-4">The Toolkit</p>
            <h2 className="font-display text-4xl md:text-5xl text-stone-100 leading-tight">
              The software <em className="italic text-[#F62493]">behind</em> the work.
            </h2>
          </div>
          <p className="md:col-span-7 text-lg text-stone-200 leading-relaxed">
            Two decades have made me deeply fluent in the tools the industry actually runs on — and quick to learn whatever new ones come next.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 md:gap-16">
          {/* Design Tools */}
          <div>
            <div className="flex items-baseline justify-between mb-6 pb-4 border-b border-stone-800">
              <h3 className="font-display text-2xl md:text-3xl text-stone-100">Design &amp; Production</h3>
              <span className="font-mono-label text-stone-300">{designTools.length} tools</span>
            </div>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3">
              {designTools.map((t, i) => (
                <li key={t} className="flex items-baseline gap-3 py-2 border-b border-stone-800 last:border-0">
                  <span className="font-mono-label text-stone-300 text-[10px]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-stone-200">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* AI Tools */}
          <div>
            <div className="flex items-baseline justify-between mb-6 pb-4 border-b border-stone-800">
              <h3 className="font-display text-2xl md:text-3xl text-stone-100 flex items-center gap-3">
                <Sparkles size={22} className="text-[#F62493]" />
                AI &amp; Emerging
              </h3>
              <span className="font-mono-label text-stone-300">{aiTools.length} tools</span>
            </div>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3 mb-6">
              {aiTools.map((t, i) => (
                <li key={t} className="flex items-baseline gap-3 py-2 border-b border-stone-800 last:border-0">
                  <span className="font-mono-label text-stone-300 text-[10px]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-stone-200">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 p-6 bg-[#171717] text-[#F7F3ED] rounded-sm">
              <p className="font-mono-label text-stone-300 mb-3 flex items-center gap-2">
                <Sparkles size={12} className="text-[#E8B0B7]" /> AI-Forward Practice
              </p>
              <p className="text-base md:text-lg leading-relaxed">{aiStatement}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="py-20 md:py-28 border-t border-stone-800">
        <div className="grid md:grid-cols-12 gap-8 mb-10">
          <div className="md:col-span-5">
            <p className="font-mono-label text-stone-300 mb-4">Capabilities</p>
            <h2 className="font-display text-4xl md:text-5xl text-stone-100">
              End-to-end creative — strategy to press check.
            </h2>
          </div>
          <ul className="md:col-span-7 grid sm:grid-cols-2 gap-x-6">
            {services.map((s, i) => (
              <li key={s} className="border-t border-stone-800 py-5 flex items-start gap-4">
                <span className="font-mono-label text-stone-300 pt-1">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display text-xl text-stone-100">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
